import React, { useState, useEffect, useCallback } from 'react';
import { AVAILABLE_METRICS, defaultEvaluator } from '../lib/evaluator';
import './EvaluatorManager.css';

const MODE_LABELS = {
  worst: 'Worst-of (max severity across rules)',
  any: 'Any exceeds (fails if any rule crosses threshold)',
  all: 'All must exceed (fails only if every rule crosses threshold)'
};

function EvaluatorManager({ api }) {
  const [evaluators, setEvaluators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Editor state
  const [editing, setEditing] = useState(null); // null = list view, object = editing
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchEvaluators = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getEvaluators();
      setEvaluators(data.evaluators || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchEvaluators(); }, [fetchEvaluators]);

  const handleNew = () => {
    setEditing({ ...defaultEvaluator(), _isNew: true });
  };

  const handleEdit = (ev) => {
    setEditing({ ...ev, rules: ev.rules.map(r => ({ ...r })) });
  };

  const handleCancel = () => {
    setEditing(null);
  };

  const handleSave = async () => {
    if (!editing) return;
    const { name, mode, rules } = editing;
    if (!name.trim()) { setError('Name is required'); return; }
    if (rules.length === 0) { setError('At least one rule is required'); return; }

    setSaving(true);
    setError(null);
    try {
      const payload = { name, mode, rules };
      if (editing._isNew) {
        await api.createEvaluator(payload);
      } else {
        await api.updateEvaluator(editing.id, payload);
      }
      setEditing(null);
      await fetchEvaluators();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setError(null);
      await api.deleteEvaluator(id);
      setDeleteConfirm(null);
      await fetchEvaluators();
    } catch (err) {
      setError(err.message);
    }
  };

  // Rule editing helpers
  const updateRule = (idx, field, value) => {
    setEditing(prev => {
      const rules = [...prev.rules];
      rules[idx] = { ...rules[idx], [field]: value };
      return { ...prev, rules };
    });
  };

  const addRule = () => {
    setEditing(prev => {
      const usedMetrics = new Set(prev.rules.map(r => r.metric));
      const next = AVAILABLE_METRICS.find(m => !usedMetrics.has(m.key)) || AVAILABLE_METRICS[0];
      return { ...prev, rules: [...prev.rules, { metric: next.key, low: next.defaultLow, high: next.defaultHigh }] };
    });
  };

  const removeRule = (idx) => {
    setEditing(prev => ({
      ...prev,
      rules: prev.rules.filter((_, i) => i !== idx)
    }));
  };

  if (loading) {
    return <div className="evaluator-mgr"><div className="loading-spinner small"></div></div>;
  }

  // Editor view
  if (editing) {
    return (
      <div className="evaluator-mgr">
        <div className="evaluator-mgr-header">
          <h2>{editing._isNew ? 'New Evaluator' : `Edit: ${editing.name}`}</h2>
        </div>
        {error && <div className="evaluator-mgr-error">{error}</div>}
        <div className="evaluator-editor">
          <div className="editor-field">
            <label>Name</label>
            <input
              type="text"
              value={editing.name}
              onChange={e => setEditing(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g. Shadow Budget"
              autoFocus
            />
          </div>
          <div className="editor-field">
            <label>Mode</label>
            <select
              value={editing.mode}
              onChange={e => setEditing(prev => ({ ...prev, mode: e.target.value }))}
            >
              {Object.entries(MODE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div className="editor-rules">
            <div className="editor-rules-header">
              <label>Rules</label>
              <button className="add-rule-btn" onClick={addRule}>+ Add Rule</button>
            </div>
            {editing.rules.map((rule, idx) => {
              const metricInfo = AVAILABLE_METRICS.find(m => m.key === rule.metric);
              return (
                <div key={idx} className="rule-row">
                  <select
                    value={rule.metric}
                    onChange={e => {
                      const newMetric = e.target.value;
                      const info = AVAILABLE_METRICS.find(m => m.key === newMetric);
                      updateRule(idx, 'metric', newMetric);
                      if (info) {
                        updateRule(idx, 'low', info.defaultLow);
                        updateRule(idx, 'high', info.defaultHigh);
                      }
                    }}
                  >
                    {AVAILABLE_METRICS.map(m => (
                      <option key={m.key} value={m.key}>{m.label}</option>
                    ))}
                  </select>
                  <div className="threshold-input">
                    <span className="threshold-label">good:</span>
                    <input
                      type="number"
                      value={rule.low ?? metricInfo?.defaultLow ?? 0}
                      onChange={e => updateRule(idx, 'low', Number(e.target.value))}
                    />
                  </div>
                  <div className="threshold-input">
                    <span className="threshold-label">bad:</span>
                    <input
                      type="number"
                      value={rule.high ?? metricInfo?.defaultHigh ?? 0}
                      onChange={e => updateRule(idx, 'high', Number(e.target.value))}
                    />
                  </div>
                  <button
                    className="remove-rule-btn"
                    onClick={() => removeRule(idx)}
                    disabled={editing.rules.length <= 1}
                    title="Remove rule"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
          <div className="editor-actions">
            <button className="save-btn" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button className="cancel-btn" onClick={handleCancel} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="evaluator-mgr">
      <div className="evaluator-mgr-header">
        <h2>Evaluators</h2>
        <button className="new-eval-btn" onClick={handleNew}>+ New Evaluator</button>
      </div>
      {error && <div className="evaluator-mgr-error">{error}</div>}
      {evaluators.length === 0 ? (
        <div className="evaluator-mgr-empty">
          No evaluators yet. Create one to define custom severity scoring for STF scan points.
        </div>
      ) : (
        <div className="evaluator-list">
          {evaluators.map(ev => (
            <div key={ev.id} className="evaluator-card">
              <div className="evaluator-card-header">
                <div className="evaluator-card-title">{ev.name}</div>
                <div className="evaluator-card-mode">{ev.mode}</div>
              </div>
              <div className="evaluator-card-rules">
                {ev.rules.map((r, i) => {
                  const info = AVAILABLE_METRICS.find(m => m.key === r.metric);
                  return (
                    <span key={i} className="rule-chip">
                      {info?.label || r.metric}: {(r.low ?? info?.defaultLow ?? 0).toLocaleString()}–{(r.high ?? r.threshold ?? info?.defaultHigh ?? 0).toLocaleString()}
                    </span>
                  );
                })}
              </div>
              <div className="evaluator-card-meta">
                <span>{ev.author}</span>
                {ev.createdAt && <span>{new Date(ev.createdAt).toLocaleDateString()}</span>}
              </div>
              <div className="evaluator-card-actions">
                <button className="edit-btn" onClick={() => handleEdit(ev)}>Edit</button>
                {deleteConfirm === ev.id ? (
                  <>
                    <button className="delete-confirm-btn" onClick={() => handleDelete(ev.id)}>Confirm Delete</button>
                    <button className="cancel-btn" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                  </>
                ) : (
                  <button className="delete-btn" onClick={() => setDeleteConfirm(ev.id)}>Delete</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default EvaluatorManager;
